import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CopySimple } from "@phosphor-icons/react";

export default function CopyDiscriminatorDialog({ open, onOpenChange, discriminator, sourceProtocolId, protocols, onCopied }) {
  const [targets, setTargets] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const list = protocols.filter((p) => p.protocol_id !== sourceProtocolId && (!q || p.name.toLowerCase().includes(q.toLowerCase())));

  const toggle = (id) => setTargets((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const doCopy = async () => {
    if (!discriminator || targets.length === 0) return;
    setBusy(true);
    let ok = 0, versioned = 0, failed = 0;
    for (const pid of targets) {
      try {
        const full = (await api.get(`/protocols/${pid}`)).data;
        const use = (await api.get(`/protocols/${pid}/usage`)).data;
        const clone = {
          ...JSON.parse(JSON.stringify(discriminator)),
          id: Math.random().toString(36).slice(2, 10),
          order: (full.discriminators || []).length,
        };
        const url = `/protocols/${pid}` + (use.requires_new_version ? "?force_new_version=true" : "");
        await api.put(url, { ...full, discriminators: [...(full.discriminators || []), clone] });
        ok += 1;
        if (use.requires_new_version) versioned += 1;
      } catch { failed += 1; }
    }
    setBusy(false);
    if (ok > 0) toast.success(`Copiato in ${ok} protocol${ok === 1 ? "lo" : "li"}${versioned ? ` (${versioned} nuove versioni)` : ""}`);
    if (failed > 0) toast.error(`${failed} copie fallite`);
    setTargets([]); setQ("");
    onOpenChange(false);
    onCopied?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CopySimple size={22} /> Copia discriminatore in altri protocolli
          </DialogTitle>
          <DialogDescription>
            "{discriminator?.name}" verrà aggiunto in fondo ai protocolli selezionati. Se il protocollo è già usato in triage, verrà creata una nuova versione.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca protocollo…"
          data-testid="copy-search"
          className="mb-2"
        />
        <ul className="space-y-1 max-h-72 overflow-auto border border-slate-200 rounded-md p-2">
          {list.map((p) => (
            <li key={p.protocol_id}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                onClick={() => toggle(p.protocol_id)}
                data-testid={`copy-target-${p.protocol_id}`}>
              <Checkbox checked={targets.includes(p.protocol_id)} className="pointer-events-none" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.name}</div>
                <div className="text-xs text-slate-500 truncate">v.{p.version} · {(p.discriminators || []).length} discriminatori</div>
              </div>
            </li>
          ))}
          {list.length === 0 && (
            <li className="text-sm text-slate-500 text-center py-4">Nessun protocollo disponibile</li>
          )}
        </ul>
        <div className="text-xs text-slate-500">{targets.length} selezionat{targets.length === 1 ? "o" : "i"}</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={doCopy} disabled={targets.length === 0 || busy} data-testid="copy-confirm" className="bg-slate-900 hover:bg-slate-800">
            {busy ? "Copio…" : `Copia in ${targets.length || 0}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
